import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import {
  descriptorFor,
  getProviderFields,
  integrationConfigService,
  matchingPreset,
  type IntegrationConfig,
  type ProviderDescriptor,
  type ProviderField,
} from '../../services/integration-config.service';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useMyPermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { PageSkeleton } from '../../components/common/panel';

const input =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

const Field = ({ label, hint, required, children }: {
  label: string; hint?: string | null; required?: boolean; children: React.ReactNode;
}) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
  </div>
);

/** Values are held as strings while editing and converted on save, so a
 *  half-typed number does not become NaN mid-keystroke. */
type FormValues = Record<string, string>;

const toFormValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const ProviderConfig = () => {
  const navigate = useAppNavigate();
  const params = useParams();
  const toast = useRef<Toast>(null);
  const { has } = useMyPermissions();
  const canEdit = has(PERMISSIONS.INTEGRATION_CONFIG_UPDATE);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<IntegrationConfig[]>([]);
  const [descriptors, setDescriptors] = useState<ProviderDescriptor[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [values, setValues] = useState<FormValues>({});
  /** Which secrets already have a stored value — presence only; the API never
   *  returns the value itself. Drives "leave blank to keep" vs "set one". */
  const [storedSecrets, setStoredSecrets] = useState<Record<string, boolean>>({});
  const [preset, setPreset] = useState('custom');
  const [enabled, setEnabled] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  /** Fallback editor for a provider the registry does not describe. */
  const [rawJson, setRawJson] = useState('');

  const notify = (severity: 'success' | 'error', summary: string, detail: string) =>
    toast.current?.show({ severity, summary, detail, life: severity === 'error' ? 10000 : 4000 });

  const selected = useMemo(
    () => configs.find((c) => c._id === selectedId) || null, [configs, selectedId]);

  const descriptor = useMemo(
    () => (selected ? descriptorFor(descriptors, selected.channel, selected.provider) : undefined),
    [descriptors, selected]);

  /** Load a configuration into the form. Secrets are never populated — the API
   *  returns them masked, and putting a mask in the box would save the mask. */
  const loadInto = (row: IntegrationConfig, all: ProviderDescriptor[]) => {
    const desc = descriptorFor(all, row.channel, row.provider);
    const blocks: Record<string, Record<string, unknown>> = {
      config: (row.config || {}) as Record<string, unknown>,
      webhook: (row.webhook || {}) as Record<string, unknown>,
    };

    const next: FormValues = {};
    const secrets: Record<string, boolean> = {};
    (desc?.fields || []).forEach((f) => {
      const stored = blocks[f.block]?.[f.name];
      if (f.secret) {
        secrets[f.name] = !!stored;
        next[f.name] = '';
        return;
      }
      next[f.name] = toFormValue(stored ?? f.default);
    });

    setValues(next);
    setStoredSecrets(secrets);
    setEnabled(row.enabled !== false);
    setPreset(desc ? matchingPreset(desc.presets, blocks.config) : 'custom');
    setShowAdvanced(false);
    // Only meaningful without a descriptor, but kept in sync either way so
    // switching provider never shows the previous one's JSON.
    setRawJson(JSON.stringify(row.config || {}, null, 2));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, descs] = await Promise.all([
          integrationConfigService.list(),
          getProviderFields(),
        ]);
        if (cancelled) return;
        setConfigs(rows);
        setDescriptors(descs);

        // A channel in the URL preselects it — the App Config list links straight
        // to the email provider, which is what most operators come here for.
        const wanted = (params.channel || '').toLowerCase();
        const initial = (wanted && rows.find((r) => r.channel === wanted)) || rows[0] || null;
        if (initial) {
          setSelectedId(initial._id);
          loadInto(initial, descs);
        }
      } catch (error) {
        notify('error', 'Error', extractErrorMessage(error, 'Failed to load provider configuration'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.channel]);

  const selectConfig = (id: string) => {
    const row = configs.find((c) => c._id === id);
    if (!row) return;
    setSelectedId(id);
    loadInto(row, descriptors);
  };

  const applyPreset = (key: string) => {
    setPreset(key);
    const chosen = descriptor?.presets.find((p) => p.key === key);
    if (!chosen || Object.keys(chosen.values).length === 0) return;
    setValues((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(chosen.values).map(([k, v]) => [k, toFormValue(v)])),
    }));
  };

  const setValue = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Editing a preset-controlled field means this is no longer that preset.
    if (descriptor?.presets.some((p) => name in p.values)) {
      setPreset(matchingPreset(descriptor.presets, { ...values, [name]: value }));
    }
  };

  /** Turn the string form back into the typed value the adapter expects. */
  const coerce = (field: ProviderField, raw: string): unknown => {
    if (field.type === 'number') return raw === '' ? null : Number(raw);
    if (field.type === 'boolean') return raw === 'true';
    return raw.trim();
  };

  const handleSave = async () => {
    if (!selected) return;

    const config: Record<string, unknown> = {};
    const webhook: Record<string, unknown> = {};

    if (descriptor) {
      for (const field of descriptor.fields) {
        const raw = values[field.name] ?? '';
        const target = field.block === 'webhook' ? webhook : config;

        // A blank secret means "keep the stored one". Sending it as null would
        // encrypt an empty credential and revoke a working one every time an
        // operator saved an unrelated field on this page.
        if (field.secret) {
          if (raw.trim()) {
            target[field.name] = raw.trim();
          } else if (field.required && !storedSecrets[field.name]) {
            // Nothing typed and nothing stored. Saving would write a provider
            // that cannot authenticate, and the failure would surface later as a
            // rejected send rather than here, where it is one field away.
            return notify('error', 'Validation', `${field.label} is required.`);
          }
          continue;
        }
        if (field.required && !raw.trim()) {
          return notify('error', 'Validation', `${field.label} is required.`);
        }
        if (field.type === 'number' && raw !== '' && !Number.isFinite(Number(raw))) {
          return notify('error', 'Validation', `${field.label} must be a number.`);
        }
        target[field.name] = coerce(field, raw);
      }
    } else {
      // Undescribed provider — the operator edits the object directly rather than
      // being locked out of a provider the registry has not caught up with.
      try {
        const parsed = JSON.parse(rawJson || '{}');
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          return notify('error', 'Validation', 'Configuration must be a JSON object.');
        }
        Object.assign(config, parsed);
      } catch {
        return notify('error', 'Validation', 'Configuration is not valid JSON.');
      }
    }

    const payload: Record<string, unknown> = { config, enabled };
    // Only sent when the provider actually has webhook fields, so an outbound-only
    // provider's empty block is not written over what is already there.
    if (Object.keys(webhook).length > 0) payload.webhook = webhook;

    try {
      setSaving(true);
      await integrationConfigService.update(selected._id, payload as never);
      const rows = await integrationConfigService.list();
      setConfigs(rows);
      const refreshed = rows.find((r) => r._id === selected._id);
      if (refreshed) loadInto(refreshed, descriptors);
      notify('success', 'Saved', `${selected.channel} / ${selected.provider} updated.`);
    } catch (error) {
      notify('error', 'Error', extractErrorMessage(error, 'Could not save the provider'));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: ProviderField) => {
    const raw = values[field.name] ?? '';

    if (field.type === 'boolean') {
      return (
        <Field key={field.name} label={field.label} hint={field.hint} required={field.required}>
          <Dropdown
            value={raw === 'true'}
            options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
            onChange={(e: never) => setValue(field.name, (e as { value: boolean }).value ? 'true' : 'false')}
            disabled={!canEdit}
            className="w-full"
            data-testid={`ProviderConfig.Input.${field.name}`}
          />
        </Field>
      );
    }

    if (field.type === 'select') {
      return (
        <Field key={field.name} label={field.label} hint={field.hint} required={field.required}>
          <Dropdown
            value={raw}
            options={field.options || []}
            onChange={(e: never) => setValue(field.name, String((e as { value: string }).value))}
            disabled={!canEdit}
            className="w-full"
            data-testid={`ProviderConfig.Input.${field.name}`}
          />
        </Field>
      );
    }

    const isSecret = field.type === 'password' || field.secret;
    return (
      <Field
        key={field.name}
        label={field.label}
        /* A credential is required only while none is stored. Once one is, a
           blank box means "keep the stored value", so marking it required would
           demand a re-entry the form does not actually want. It used to be
           suppressed for every secret, which left a genuinely mandatory Auth key
           looking optional on a provider that had never been configured. */
        required={field.required && (!isSecret || !storedSecrets[field.name])}
        hint={isSecret && storedSecrets[field.name]
          ? 'A value is stored. Leave blank to keep it; type to replace.'
          : field.hint}
      >
        <input
          className={input}
          type={isSecret ? 'password' : 'text'}
          autoComplete={isSecret ? 'new-password' : 'off'}
          inputMode={field.type === 'number' ? 'numeric' : undefined}
          value={raw}
          onChange={(e) => setValue(field.name, e.target.value)}
          placeholder={isSecret && storedSecrets[field.name]
            ? '••••••••  (unchanged)'
            : field.placeholder || ''}
          disabled={!canEdit}
          data-testid={`ProviderConfig.Input.${field.name}`}
        />
      </Field>
    );
  };

  if (loading) return <PageSkeleton panels={1} />;

  const basic = (descriptor?.fields || []).filter((f) => !f.advanced);
  const advanced = (descriptor?.fields || []).filter((f) => f.advanced);
  const activePreset = descriptor?.presets.find((p) => p.key === preset);

  return (
    <PermissionGuard jobName={JOB_NAMES.APP_CONFIG}>
      <div className="p-6" data-testid="SCR-ProviderConfig">
        <Toast ref={toast} />
        <PageHeader
          icon="pi pi-sliders-h"
          title="Integration Providers"
          subtitle="Connection settings for each gateway. Every provider needs different fields, so the form follows the provider you pick."
          testId="ProviderConfig.Header"
        />

        {configs.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            No provider is configured yet. Seed one with the migration scripts, or
            create it through the Integration Config API.
          </div>
        ) : (
          <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <Field label="Provider" hint="Each row is one channel and provider pair.">
              <Dropdown
                value={selectedId}
                options={configs.map((c) => ({
                  label: `${c.channel} / ${c.provider}${c.isDefault ? '  (default)' : ''}`,
                  value: c._id,
                }))}
                onChange={(e: never) => selectConfig((e as { value: string }).value)}
                className="w-full"
                data-testid="ProviderConfig.Input.Config"
              />
            </Field>

            {descriptor && descriptor.presets.length > 0 && (
              <Field
                label="Known providers"
                hint={activePreset?.hint
                  || 'A shortcut for the fields below — all of them stay editable.'}
              >
                <Dropdown
                  value={preset}
                  options={descriptor.presets.map((p) => ({ label: p.label, value: p.key }))}
                  onChange={(e: never) => applyPreset((e as { value: string }).value)}
                  disabled={!canEdit}
                  className="w-full"
                  data-testid="ProviderConfig.Input.Preset"
                />
              </Field>
            )}

            {descriptor ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {basic.map(renderField)}
              </div>
            ) : (
              <Field
                label="Configuration (JSON)"
                hint={`No field description is published for ${selected?.channel} / ${selected?.provider}, so its settings are edited directly.`}
              >
                <textarea
                  className={`${input} font-mono text-xs`}
                  rows={12}
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  disabled={!canEdit}
                  data-testid="ProviderConfig.Input.RawJson"
                />
              </Field>
            )}

            {advanced.length > 0 && (
              <div className="rounded border border-dashed border-gray-300 p-3 dark:border-gray-600">
                <button
                  type="button"
                  className="text-xs font-medium text-blue-600 hover:underline"
                  onClick={() => setShowAdvanced((v) => !v)}
                  data-testid="ProviderConfig.Button.Advanced"
                >
                  {showAdvanced ? 'Hide' : 'Show'} advanced settings ({advanced.length})
                </button>
                <p className="mt-1 text-[11px] text-gray-500">
                  These have working defaults. Change them only when the provider
                  changes its API.
                </p>
                {showAdvanced && (
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {advanced.map(renderField)}
                  </div>
                )}
              </div>
            )}

            <Field
              label="Status"
              hint="Disabled refuses every send outright — it does not fall back to anything."
            >
              <Dropdown
                value={enabled}
                options={[{ label: 'Enabled', value: true }, { label: 'Disabled', value: false }]}
                onChange={(e: never) => setEnabled((e as { value: boolean }).value)}
                disabled={!canEdit}
                className="w-full"
                data-testid="ProviderConfig.Input.Enabled"
              />
            </Field>

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button
                label="Back" severity="secondary" text
                onClick={() => navigate('/app-config')}
                testId="ProviderConfig.Button.Back"
              />
              {canEdit && (
                <Button
                  label="Save" icon="pi pi-check" loading={saving} onClick={handleSave}
                  testId="ProviderConfig.Button.Save"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default ProviderConfig;
